import React, { useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { setCurrentLivestockSaleId } from "actions";

import { ClickableCommentIcon } from "components/Comments/Icon";
import { PreventPropagation } from "components/Events";
import { Column, Row } from "components/Layout";
import { Text } from "components/Text";

import { CommentTypes } from "constants/comments";
import { getIconForSpeciesId } from "constants/species";

import { formatHeaderDateString } from "lib/timeFormats";

import {
  getAgentsDeploymentSaleBySaleId,
  getEffectiveSaleTitleBySaleId,
  getIsLivestockAgent,
  getIsOnline,
  getSaleById,
  selectCanCreateExternalAgentSaleFromSale,
} from "selectors";
import {
  useCanCreateSellViaExAgentSaleFromSale,
  useIsMobile,
  useIsSmallMobile,
} from "hooks";
import { faCopy } from "@fortawesome/pro-solid-svg-icons";
import Button from "components/Button";
import {
  openCreateExAgentSaleModal,
  openCreateSellViaExAgentSaleModal,
} from "lib/navigation";
import { ContextMenuPopoverButton } from "components/Button/MultiButton";
import { SaleStatusComponent } from "./StatusSelect";

const IconContainer = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.delta}px;
  width: 42px;
  height: 42px;
  background: ${({ theme }) => theme.colors.grayDisabled};
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${({ theme }) => theme.space[1]}px;
`;

const SaleRowColumns = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
  font-weight: normal;
  margin: auto ${({ theme }) => theme.space[2]}px;
  @media (max-width: ${({ theme }) => theme.breakpoints[1]}px) {
    margin: auto ${({ theme }) => theme.space[1]}px;
  }
  color: ${({ theme }) => theme.colors.gray40};
  flex-basis: 0;
  flex-grow: 1;
  justify-content: space-between;
`;

const SaleRow = styled(Row)`
  box-shadow: ${({ theme }) => theme.shadows[1]};
  padding: 10px;
  margin: ${({ isMobile }) => (!isMobile ? "4px 0px 4px" : "2px -20px 2px")};
  position: relative;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.colors.primaryHighlight}20;
  }
`;

const BlueBar = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  width: 10px;
  margin-right: 5px;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
`;

const MultiButtonComponent = styled(Button)(
  ({ theme }) => `
  padding: ${theme.space[0]}px ${theme.space[2]}px;
  border-radius: 5px;
`,
);

const ClampedSaleTitle = styled(Text)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const CompactSaleMetadata = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.beta}px;
  color: ${({ theme }) => theme.colors.gray40};
  margin-top: ${({ theme }) => theme.space[1]}px;
`;

const TruncatingStatusSlot = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;

  /* Fallback SaleStatusBadge defaults to margin: auto (for AG Grid cell
     centering) and white-space: nowrap. Reset margin so it sits flush left,
     and constrain width so a long status truncates with ellipsis instead of
     pushing the comment icon off the card. */
  > span {
    margin: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const CommentIconSlot = styled.div`
  flex: 0 0 auto;
  margin-left: ${({ theme }) => theme.space[2]}px;
`;

interface DashboardSaleCardProps {
  saleId: number;
}

const DashboardSaleCard = React.memo(
  ({ saleId }: DashboardSaleCardProps): React.JSX.Element => {
    const dispatch = useDispatch();
    const livestockSale = useSelector(getSaleById(saleId));
    const saleDate = livestockSale ? new Date(livestockSale.date) : null;

    const isMobile = useIsMobile();
    const isSmallMobile = useIsSmallMobile();

    const namedDate = saleDate
      ? formatHeaderDateString(saleDate, !isMobile)
      : "";

    const isLivestockAgent = useSelector(getIsLivestockAgent);

    const canCreateExternalAgentSaleFromSale = useSelector(
      selectCanCreateExternalAgentSaleFromSale,
    );

    const deploymentSale = useSelector(getAgentsDeploymentSaleBySaleId(saleId));

    const saleTitle = useSelector(getEffectiveSaleTitleBySaleId(saleId));

    const isOnline = useSelector(getIsOnline);

    const goToSale = () =>
      dispatch(setCurrentLivestockSaleId(saleId, true, false));

    const handleParentClick = event => {
      if (!event.defaultPrevented) {
        goToSale();
      }
    };

    const canCreateSellViaExternalAgentSaleFromSale =
      useCanCreateSellViaExAgentSaleFromSale(saleId);

    const additionalActions = useMemo(
      () =>
        [
          canCreateExternalAgentSaleFromSale
            ? {
                title: "Create Ex Agent Sale",
                icon: faCopy,
                isDisabled: !isOnline,
                onClick: () => openCreateExAgentSaleModal(saleId),
              }
            : null,
          canCreateSellViaExternalAgentSaleFromSale
            ? {
                title: "Create Sell via Ex Agent Sale",
                icon: faCopy,
                isDisabled: !isOnline,
                onClick: () => openCreateSellViaExAgentSaleModal(saleId),
              }
            : null,
        ].filter(Boolean),
      [
        canCreateExternalAgentSaleFromSale,
        canCreateSellViaExternalAgentSaleFromSale,
        isOnline,
        saleId,
      ],
    );

    if (!livestockSale) {
      return null;
    }

    return (
      <Column data-tour="saleCard">
        <SaleRow
          data-tour={livestockSale.sale_code}
          sale={livestockSale}
          isMobile={isMobile}
          onClick={handleParentClick}
        >
          {!isMobile && <BlueBar />}
          <IconContainer isMobile={isMobile}>
            <FontAwesomeIcon
              icon={getIconForSpeciesId(livestockSale?.species_id)}
            />
          </IconContainer>
          {isSmallMobile ? (
            <Column flexGrow flexShrink basis="0%" minWidth="0">
              <Row fullWidth alignCenter justifyBetween gridGap={1}>
                <ClampedSaleTitle fontSize="charlie">
                  {saleTitle}
                </ClampedSaleTitle>
                {/* Top-right slot reserved for a future action icon. */}
              </Row>
              <CompactSaleMetadata>
                {[namedDate, livestockSale.sale_type, livestockSale.sale_code]
                  .filter(Boolean)
                  .join(" · ")}
              </CompactSaleMetadata>
              <Row fullWidth alignCenter justifyBetween marginTop={1}>
                <TruncatingStatusSlot>
                  <SaleStatusComponent saleId={saleId} />
                </TruncatingStatusSlot>
                {isLivestockAgent && (
                  <CommentIconSlot>
                    <PreventPropagation>
                      <ClickableCommentIcon
                        commentType={CommentTypes.DEPLOYMENT_SALE}
                        commentTypeId={deploymentSale.deployment_sale_id}
                        returnTo={window.location.hash}
                        options={{}}
                      />
                    </PreventPropagation>
                  </CommentIconSlot>
                )}
              </Row>
            </Column>
          ) : (
            <Grid alignItems="center" container>
              <Grid item xs={12} sm={3} md={3} lg={3}>
                <Text marginHorizontal={1} fontSize="charlie">
                  {saleTitle}
                </Text>
              </Grid>
              <Grid item xs={2}>
                <SaleRowColumns>{namedDate}</SaleRowColumns>
              </Grid>
              <Grid item xs={2}>
                <SaleRowColumns>{livestockSale.sale_type}</SaleRowColumns>
              </Grid>
              <Grid item xs={1}>
                <SaleRowColumns>{livestockSale.sale_code}</SaleRowColumns>
              </Grid>
              {isLivestockAgent && (
                <Grid item xs={1}>
                  <SaleRowColumns>
                    <PreventPropagation>
                      <ClickableCommentIcon
                        commentType={CommentTypes.DEPLOYMENT_SALE}
                        commentTypeId={deploymentSale.deployment_sale_id}
                        returnTo={window.location.hash}
                        options={{}}
                      />
                    </PreventPropagation>
                  </SaleRowColumns>
                </Grid>
              )}
              <Grid item xs={2}>
                <SaleRowColumns>
                  <SaleStatusComponent saleId={saleId} />
                </SaleRowColumns>
              </Grid>
              {additionalActions.length > 0 && (
                <Grid item xs={1}>
                  <PreventPropagation>
                    <ContextMenuPopoverButton
                      buttons={additionalActions}
                      label=""
                      ButtonComponent={MultiButtonComponent}
                    />
                  </PreventPropagation>
                </Grid>
              )}
            </Grid>
          )}
        </SaleRow>
      </Column>
    );
  },
);

export default DashboardSaleCard;
